/* ============================================================
   MOONLIGHT MOTIFS — theme.js
   Premium interactive behaviors
   ============================================================ */

'use strict';

/* ── UTILITIES ───────────────────────────────────────────────── */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function debounce(fn, wait = 200) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
}

/* ── SCROLL ANIMATIONS ───────────────────────────────────────── */
function initScrollAnimations() {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.05, rootMargin: '0px 0px -80px 0px' });

  $$('.fade-up').forEach(el => observer.observe(el));
}

/* ── STICKY HEADER ───────────────────────────────────────────── */
function initStickyHeader() {
  const header = $('.site-header');
  if (!header) return;

  const onScroll = debounce(() => {
    header.classList.toggle('scrolled', window.scrollY > 60);
  }, 10);

  window.addEventListener('scroll', onScroll, { passive: true });
}

/* ── MOBILE NAVIGATION ───────────────────────────────────────── */
function initMobileNav() {
  const toggleBtn = $('#mobileMenuBtn');
  const closeBtn = $('#mobileNavClose');
  const nav = $('#mobileNav');
  const overlay = $('#pageOverlay');
  if (!toggleBtn || !nav) return;

  function open() {
    toggleBtn.classList.add('open');
    nav.classList.add('open');
    overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
    toggleBtn.setAttribute('aria-expanded', 'true');
  }
  function close() {
    toggleBtn.classList.remove('open');
    nav.classList.remove('open');
    overlay.classList.remove('active');
    document.body.style.overflow = '';
    toggleBtn.setAttribute('aria-expanded', 'false');
  }

  toggleBtn.addEventListener('click', () => nav.classList.contains('open') ? close() : open());
  if (closeBtn) closeBtn.addEventListener('click', close);
  overlay.addEventListener('click', close);

  document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
}

/* ── SEARCH BAR ──────────────────────────────────────────────── */
function initSearch() {
  const openBtn = $('#searchBtn');
  const closeBtn = $('#searchClose');
  const searchBar = $('#searchBar');
  const input = $('#searchInput');
  if (!openBtn || !searchBar) return;

  openBtn.addEventListener('click', () => {
    searchBar.classList.add('open');
    input?.focus();
  });
  closeBtn?.addEventListener('click', () => searchBar.classList.remove('open'));
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') searchBar.classList.remove('open');
  });
}

const CHECKOUT_URL = '/checkout';

/* ── CART DRAWER ─────────────────────────────────────────────── */
function initCartDrawer() {
  const drawer = $('#cartDrawer');
  const openBtns = $$('[data-cart-open]');
  const closeBtn = $('#cartClose');
  const overlay = $('#cartOverlay');
  if (!drawer) return;

  function openCart() {
    drawer.classList.add('open');
    document.body.style.overflow = 'hidden';
  }
  function closeCart() {
    drawer.classList.remove('open');
    document.body.style.overflow = '';
  }

  openBtns.forEach(btn => btn.addEventListener('click', openCart));
  closeBtn?.addEventListener('click', closeCart);
  overlay?.addEventListener('click', closeCart);
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeCart(); });

  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href="/cart"]');
    if (link) {
      e.preventDefault();
      openCart();
      return;
    }

    const checkoutLink = e.target.closest('a[href="/checkout"]');
    if (checkoutLink) {
      return;
    }
  });

  if (window.location.pathname === '/cart') {
    openCart();
    history.replaceState(null, '', '/');
  }
}

/* ── BUY NOW ─────────────────────────────────────────────────── */
function getProductVariantId() {
  const input = $('#variantId');
  if (input?.value) return parseInt(input.value, 10);

  const atcBtn = $('[data-add-to-cart]');
  if (atcBtn?.dataset.variantId) return parseInt(atcBtn.dataset.variantId, 10);

  return null;
}

function initBuyNow() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-buy-now]');
    if (!btn) return;

    e.preventDefault();

    const variantId = getProductVariantId();
    if (!variantId) {
      showToast('Please select all options before continuing.', true);
      return;
    }

    btn.classList.add('btn--loading');
    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = 'Processing...';

    try {
      try {
        await fetch('/cart/clear.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
      } catch {}

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
      });

      if (!res.ok) throw new Error('Failed to add');

      window.location.href = CHECKOUT_URL;
    } catch {
      showToast('Something went wrong. Please try again.', true);
      btn.classList.remove('btn--loading');
      btn.disabled = false;
      btn.textContent = originalText;
    }
  });
}

/* ── PLACE ORDER PAGE ────────────────────────────────────────── */
function initPlaceOrder() {
  const form = document.getElementById('placeOrderForm');
  if (!form) return;

  const itemsEl = document.getElementById('placeOrderItems');
  const totalsEl = document.getElementById('placeOrderTotals');
  const subtotalEl = document.getElementById('placeOrderSubtotal');
  const totalEl = document.getElementById('placeOrderTotal');
  const submitBtn = document.getElementById('placeOrderSubmit');
  const errorEl = document.getElementById('placeOrderError');
  const enableHintEl = document.getElementById('placeOrderEnableHint');
  const deliveryFee = 35000; // PKR 350 in cents
  let cartHasItems = false;

  function formatPKR(cents) {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  }

  function setError(message) {
    if (!errorEl) return;
    if (message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    } else {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  }

  function isFormValid() {
    return form.checkValidity();
  }

  function updateSubmitState() {
    const formValid = isFormValid();
    const canSubmit = cartHasItems && formValid;
    if (submitBtn) submitBtn.disabled = !canSubmit;
    if (enableHintEl) {
      if (!cartHasItems) {
        enableHintEl.textContent = 'Add items to your bag before placing an order.';
        enableHintEl.hidden = false;
      } else if (!formValid) {
        enableHintEl.textContent = 'Fill in all delivery fields to enable Place Order.';
        enableHintEl.hidden = false;
      } else {
        enableHintEl.hidden = true;
      }
    }
  }

  async function loadCart() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();

      if (!cart.item_count) {
        cartHasItems = false;
        if (itemsEl) {
          itemsEl.innerHTML = '<p class="place-order-page__empty">Your bag is empty. <a href="/collections/all">Continue shopping</a></p>';
        }
        updateSubmitState();
        return;
      }

      cartHasItems = true;

      if (itemsEl) {
        itemsEl.innerHTML = cart.items.map((item) => `
          <div class="place-order-page__item">
            <img src="${item.image}" alt="" width="64" height="80" loading="lazy">
            <div>
              <div class="place-order-page__item-title">${item.product_title}</div>
              ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="place-order-page__item-meta">${item.variant_title}</div>` : ''}
              <div class="place-order-page__item-meta">Qty ${item.quantity} · ${formatPKR(item.final_line_price)}</div>
            </div>
          </div>
        `).join('');
      }

      if (totalsEl) totalsEl.hidden = false;
      if (subtotalEl) subtotalEl.textContent = formatPKR(cart.items_subtotal_price);
      if (totalEl) totalEl.textContent = formatPKR(cart.items_subtotal_price + deliveryFee);
      updateSubmitState();
    } catch {
      if (itemsEl) itemsEl.innerHTML = '<p class="place-order-page__empty">Could not load your bag. Please try again.</p>';
    }
  }

  form.querySelectorAll('input, select').forEach((field) => {
    field.addEventListener('input', updateSubmitState);
    field.addEventListener('change', updateSubmitState);
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    setError('');

    if (!isFormValid()) {
      form.reportValidity();
      updateSubmitState();
      return;
    }

    const firstName = form.first_name.value.trim();
    const lastName = form.last_name.value.trim();
    const email = form.email.value.trim();
    const phone = form.phone.value.trim();
    const address1 = form.address1.value.trim();
    const city = form.city.value.trim();
    const province = form.province.value;

    submitBtn.disabled = true;
    submitBtn.textContent = 'Processing...';

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          note: `Phone: ${phone}`,
          attributes: {
            Phone: phone,
            'Delivery Address': `${address1}, ${city}`,
          },
        }),
      });

      const params = new URLSearchParams({
        'checkout[email]': email,
        'checkout[shipping_address][first_name]': firstName,
        'checkout[shipping_address][last_name]': lastName,
        'checkout[shipping_address][address1]': address1,
        'checkout[shipping_address][city]': city,
        'checkout[shipping_address][province]': province,
        'checkout[shipping_address][country]': 'PK',
        'checkout[shipping_address][phone]': phone,
      });

      window.location.href = `/checkout?${params.toString()}`;
    } catch {
      setError('Something went wrong. Please try again.');
      submitBtn.disabled = false;
      submitBtn.textContent = 'Place Order';
      updateSubmitState();
    }
  });

  loadCart();
}

/* ── ADD TO CART ─────────────────────────────────────────────── */
function initAddToCart() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-add-to-cart]');
    if (!btn) return;

    const variantId = btn.dataset.variantId || getProductVariantId();
    if (!variantId) return;

    btn.classList.add('btn--loading');
    const originalText = btn.textContent;
    btn.textContent = 'Adding...';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
      });

      if (!res.ok) throw new Error('Failed to add');

      await updateCartCount();
      showToast('Added to your bag!');

      // Open cart drawer
      const drawer = $('#cartDrawer');
      if (drawer) {
        drawer.classList.add('open');
        document.body.style.overflow = 'hidden';
      }
    } catch (err) {
      showToast('Something went wrong. Please try again.', true);
    } finally {
      btn.classList.remove('btn--loading');
      btn.textContent = originalText;
    }
  });
}

/* ── CART COUNT UPDATE ───────────────────────────────────────── */
async function updateCartCount() {
  try {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    $$('.cart-count').forEach(el => {
      el.textContent = cart.item_count > 0 ? cart.item_count : '';
    });
  } catch {}
}

/* ── TOAST ───────────────────────────────────────────────────── */
let toastTimeout;
function showToast(msg = 'Done!', isError = false) {
  const toast = $('#toast');
  const msgEl = $('#toastMsg');
  if (!toast || !msgEl) return;

  msgEl.textContent = msg;
  toast.style.borderColor = isError ? 'var(--color-error)' : 'var(--color-gold)';
  toast.classList.add('show');

  clearTimeout(toastTimeout);
  toastTimeout = setTimeout(() => toast.classList.remove('show'), 3000);
}

/* ── HERO BANNER ─────────────────────────────────────────────── */
function initHero() {
  const hero = $('.hero');
  if (!hero) return;

  const slider = $('[data-hero-slider]', hero);
  const slides = $$('[data-hero-slide]', hero);
  const dotsWrap = $('[data-hero-dots]', hero);

  if (slides.length) {
    let current = 0;
    let timer = null;
    const speed = (parseInt(slider?.dataset.autoplay, 10) || 5) * 1000;

    const markLoaded = () => hero.classList.add('loaded');
    const firstImg = slides[0]?.querySelector('img');
    if (firstImg) {
      if (firstImg.complete) markLoaded();
      else firstImg.addEventListener('load', markLoaded, { once: true });
    } else {
      markLoaded();
    }

    const goTo = (index) => {
      slides[current]?.classList.remove('is-active');
      dotsWrap?.children[current]?.classList.remove('active');
      current = (index + slides.length) % slides.length;
      slides[current]?.classList.add('is-active');
      dotsWrap?.children[current]?.classList.add('active');
    };

    if (dotsWrap) {
      dotsWrap.innerHTML = '';
      slides.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = `hero__slider-dot${i === 0 ? ' active' : ''}`;
        dot.setAttribute('aria-label', `Show slide ${i + 1}`);
        dot.addEventListener('click', () => {
          goTo(i);
          restart();
        });
        dotsWrap.appendChild(dot);
      });
    }

    const next = () => goTo(current + 1);

    const restart = () => {
      if (timer) clearInterval(timer);
      if (slides.length > 1) timer = setInterval(next, speed);
    };

    if (slides.length > 1) {
      restart();
      slider?.addEventListener('mouseenter', () => clearInterval(timer));
      slider?.addEventListener('mouseleave', restart);
    }
  }

  window.addEventListener('scroll', debounce(() => {
    const scrolled = window.scrollY;
    if (scrolled < window.innerHeight) {
      const bg = hero.querySelector('.hero__slider') || hero.querySelector('.hero__bg');
      if (bg) bg.style.transform = `translateY(${scrolled * 0.3}px)`;
    }
  }, 5), { passive: true });
}

/* ── PRODUCT TABS (product page) ─────────────────────────────── */
function initProductTabs() {
  $$('.product-tab-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.tab;
      const container = btn.closest('.product-tabs');
      if (!container) return;

      $$('.product-tab-btn', container).forEach(b => b.classList.remove('active'));
      $$('.product-tab-content', container).forEach(c => c.classList.remove('active'));
      btn.classList.add('active');
      const content = container.querySelector(`[data-tab-content="${target}"]`);
      content?.classList.add('active');
    });
  });
}

/* ── PRODUCT GALLERY ─────────────────────────────────────────── */
function initProductGallery() {
  const mainImg = $('.product-gallery__main-img');
  const mainWrap = $('.product-gallery__main');
  const thumbs = $$('.gallery-thumb');
  if (!mainImg || !thumbs.length) return;

  const sources = thumbs.map((thumb) => thumb.dataset.src).filter(Boolean);
  let currentIndex = thumbs.findIndex((thumb) => thumb.classList.contains('active'));
  if (currentIndex < 0) currentIndex = 0;

  const setActiveImage = (index) => {
    const safeIndex = Math.min(Math.max(index, 0), sources.length - 1);
    const src = sources[safeIndex];
    if (!src) return;

    currentIndex = safeIndex;
    mainImg.src = src;

    thumbs.forEach((thumb, i) => {
      const isActive = i === safeIndex;
      thumb.classList.toggle('active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    if (window.matchMedia('(max-width: 768px)').matches) {
      thumbs[safeIndex]?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }
  };

  thumbs.forEach((thumb, index) => {
    thumb.addEventListener('click', () => setActiveImage(index));
  });

  if (mainWrap && sources.length > 1) {
    let touchStartX = 0;
    let touchStartY = 0;

    mainWrap.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });

    mainWrap.addEventListener('touchend', (e) => {
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const diffX = touchEndX - touchStartX;
      const diffY = touchEndY - touchStartY;

      if (Math.abs(diffX) < 45 || Math.abs(diffX) < Math.abs(diffY)) return;

      if (diffX < 0) {
        setActiveImage(currentIndex + 1);
      } else {
        setActiveImage(currentIndex - 1);
      }
    }, { passive: true });
  }
}

/* ── COLLECTION FILTERS ──────────────────────────────────────── */
function initCollectionFilters() {
  const page = $('.collection-page');
  if (!page) return;

  const grid = $('#collectionGrid', page);
  const sortSelect = $('#sortBy', page);
  const filterBtns = $$('.filter-btn', page);
  const countEl = $('.collection-count', page);

  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      window.location.href = sortSelect.value;
    });
  }

  if (!grid || !filterBtns.length) return;

  const cards = () => $$('.product-card--catalog', grid);
  const totalCount = cards().length;

  filterBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      const filter = btn.dataset.filter || 'all';

      filterBtns.forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');

      let visible = 0;
      cards().forEach((card) => {
        const tags = (card.dataset.filterTags || card.textContent || '').toLowerCase();
        const show = filter === 'all' || tags.includes(filter);
        card.hidden = !show;
        card.style.display = show ? '' : 'none';
        if (show) visible += 1;
      });

      if (countEl) {
        const count = filter === 'all' ? totalCount : visible;
        countEl.textContent = `${count} suit${count === 1 ? '' : 's'}`;
      }
    });
  });
}

/* ── FEATURED SUITS CAROUSEL ─────────────────────────────────── */
function initFeaturedCarousel() {
  const carousel = $('.showcase-carousel');
  if (!carousel) return;

  const viewport = carousel.querySelector('[data-carousel-viewport]');
  const track = carousel.querySelector('[data-carousel-track]');
  const prevBtn = carousel.querySelector('.showcase-carousel__btn--prev');
  const nextBtn = carousel.querySelector('.showcase-carousel__btn--next');
  const dotsWrap = carousel.querySelector('[data-carousel-dots]');
  const slides = track ? Array.from(track.children) : [];

  if (!viewport || !track || slides.length === 0) return;

  let index = 0;
  let autoplayTimer = null;
  let slideStep = 0;

  function getSlideStep() {
    const slide = slides[0];
    if (!slide) return 0;
    const gap = parseFloat(getComputedStyle(track).gap) || 0;
    return slide.offsetWidth + gap;
  }

  function getMaxIndex() {
    slideStep = getSlideStep();
    if (!slideStep) return 0;
    const visible = Math.max(1, Math.floor(viewport.clientWidth / slideStep));
    return Math.max(0, slides.length - visible);
  }

  function updateDots() {
    if (!dotsWrap) return;
    dotsWrap.innerHTML = '';
    const max = getMaxIndex();
    for (let i = 0; i <= max; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `showcase-carousel__dot${i === index ? ' active' : ''}`;
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => {
        goTo(i);
        startAutoplay();
      });
      dotsWrap.appendChild(dot);
    }
  }

  function goTo(nextIndex, smooth = true) {
    const max = getMaxIndex();
    index = Math.min(Math.max(nextIndex, 0), max);
    viewport.scrollTo({
      left: getSlideStep() * index,
      behavior: smooth ? 'smooth' : 'auto'
    });
    updateDots();
  }

  function next() { goTo(index + 1); }
  function prev() { goTo(index - 1); }

  function startAutoplay() {
    stopAutoplay();
    autoplayTimer = setInterval(() => {
      const max = getMaxIndex();
      goTo(index >= max ? 0 : index + 1);
    }, 4500);
  }

  function stopAutoplay() {
    if (autoplayTimer) clearInterval(autoplayTimer);
    autoplayTimer = null;
  }

  prevBtn?.addEventListener('click', () => { prev(); startAutoplay(); });
  nextBtn?.addEventListener('click', () => { next(); startAutoplay(); });

  carousel.addEventListener('mouseenter', stopAutoplay);
  carousel.addEventListener('mouseleave', startAutoplay);
  carousel.addEventListener('focusin', stopAutoplay);
  carousel.addEventListener('focusout', startAutoplay);

  viewport.addEventListener('scroll', debounce(() => {
    const step = getSlideStep();
    if (!step) return;
    index = Math.round(viewport.scrollLeft / step);
    updateDots();
  }, 80), { passive: true });

  window.addEventListener('resize', debounce(() => {
    goTo(Math.min(index, getMaxIndex()), false);
  }, 150));

  goTo(0, false);
  startAutoplay();
}

/* ── PRODUCT TABS (homepage showcase) ────────────────────────── */
function initShowcaseTabs() {
  $$('.products-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      const target = tab.dataset.tab;
      $$('.products-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      $$('[data-tab-panel]').forEach(panel => {
        panel.style.display = panel.dataset.tabPanel === target ? 'grid' : 'none';
      });
    });
  });
}

/* ── WISHLIST ────────────────────────────────────────────────── */
function getWishlist() {
  try {
    return JSON.parse(localStorage.getItem('mm_wishlist') || '[]');
  } catch {
    return [];
  }
}

function setWishlist(list) {
  localStorage.setItem('mm_wishlist', JSON.stringify(list));
  document.dispatchEvent(new CustomEvent('wishlist_updated'));
}

function formatProductMoney(cents) {
  if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
  return `PKR ${(cents / 100).toLocaleString()}`;
}

function updateWishlistCount() {
  const count = getWishlist().length;
  $$('#wishlistCount, .wishlist-count').forEach(el => {
    el.textContent = count > 0 ? count : '';
    el.style.display = count > 0 ? '' : 'none';
  });
  const drawerCount = $('#wishlistDrawerCount');
  if (drawerCount) drawerCount.textContent = count;
}

async function fetchWishlistProducts() {
  const wishlist = getWishlist();
  if (!wishlist.length) return [];

  const products = await Promise.all(
    wishlist.map(async (handle) => {
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) return null;
        return await res.json();
      } catch {
        return null;
      }
    })
  );

  return products.filter(Boolean);
}

function buildWishlistDrawerItem(product) {
  const item = document.createElement('div');
  item.className = 'wishlist-item';
  item.innerHTML = `
    <a href="/products/${product.handle}" class="wishlist-item__image">
      <img src="${product.featured_image}" alt="${product.title.replace(/"/g, '&quot;')}" loading="lazy" width="80" height="100">
    </a>
    <div class="wishlist-item__details">
      <a href="/products/${product.handle}" class="wishlist-item__title">${product.title}</a>
      <p class="wishlist-item__price">${formatProductMoney(product.price)}</p>
      <button type="button" class="btn btn--outline btn--sm" data-add-to-cart data-variant-id="${product.variants[0]?.id}">Add to Bag</button>
    </div>
    <button type="button" class="wishlist-item__remove" data-wishlist-remove data-handle="${product.handle}" aria-label="Remove from wishlist">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M18 6L6 18M6 6l12 12"/>
      </svg>
    </button>
  `;
  return item;
}

function buildWishlistPageCard(product) {
  const card = document.createElement('article');
  card.className = 'product-card product-card--catalog';
  card.dataset.productHandle = product.handle;
  const compare = product.compare_at_price > product.price;
  const priceHtml = compare
    ? `<span class="product-card__price-single">${formatProductMoney(product.price)}</span>`
    : `<span class="product-card__price-single">${formatProductMoney(product.price)}</span>`;

  card.innerHTML = `
    <a href="/products/${product.handle}" class="product-card__image-wrap">
      <span class="product-card__badge badge--new">Saved</span>
      <img class="product-card__image" src="${product.featured_image}" alt="${product.title.replace(/"/g, '&quot;')}" loading="lazy" width="600" height="800">
      ${product.images.length > 1 ? `<img class="product-card__image-alt" src="${product.images[1]}" alt="" loading="lazy" width="600" height="800" aria-hidden="true">` : ''}
    </a>
    <div class="product-card__info product-card__info--catalog">
      <h3 class="product-card__title"><a href="/products/${product.handle}">${product.title}</a></h3>
      <div class="product-card__prices product-card__prices--catalog">${priceHtml}</div>
    </div>
  `;
  return card;
}

async function renderWishlistDrawer() {
  const listEl = $('#wishlistDrawerItems');
  const emptyEl = $('#wishlistDrawerEmpty');
  const footerEl = $('#wishlistDrawerFooter');
  if (!listEl || !emptyEl) return;

  const products = await fetchWishlistProducts();
  listEl.innerHTML = '';

  if (!products.length) {
    emptyEl.hidden = false;
    listEl.hidden = true;
    footerEl && (footerEl.hidden = true);
    return;
  }

  emptyEl.hidden = true;
  listEl.hidden = false;
  footerEl && (footerEl.hidden = false);
  products.forEach(product => listEl.appendChild(buildWishlistDrawerItem(product)));
}

async function renderWishlistPage() {
  const grid = $('#wishlistPageGrid');
  const emptyEl = $('#wishlistPageEmpty');
  if (!grid || !emptyEl) return;

  const products = await fetchWishlistProducts();
  grid.innerHTML = '';

  if (!products.length) {
    emptyEl.hidden = false;
    grid.hidden = true;
    return;
  }

  emptyEl.hidden = true;
  grid.hidden = false;
  products.forEach(product => grid.appendChild(buildWishlistPageCard(product)));
}

function initWishlistDrawer() {
  const drawer = $('#wishlistDrawer');
  const openBtns = $$('[data-wishlist-open]');
  const closeBtn = $('#wishlistClose');
  const overlay = $('#wishlistOverlay');
  if (!drawer) return;

  function openWishlist() {
    renderWishlistDrawer();
    drawer.classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  function closeWishlist() {
    drawer.classList.remove('open');
    document.body.style.overflow = '';
  }

  openBtns.forEach(btn => btn.addEventListener('click', openWishlist));
  closeBtn?.addEventListener('click', closeWishlist);
  overlay?.addEventListener('click', closeWishlist);

  document.addEventListener('click', (e) => {
    const removeBtn = e.target.closest('[data-wishlist-remove]');
    if (!removeBtn) return;
    const handle = removeBtn.dataset.handle;
    if (!handle) return;
    setWishlist(getWishlist().filter(item => item !== handle));
    showToast('Removed from wishlist');
  });

  document.addEventListener('wishlist_updated', () => {
    updateWishlistCount();
    renderWishlistDrawer();
    renderWishlistPage();
  });

  updateWishlistCount();
  renderWishlistPage();
}

/* ── WISHLIST TOGGLE ─────────────────────────────────────────── */
function initWishlist() {
  const wishlist = getWishlist();
  $$('.product-card__wish, [data-wishlist]').forEach(btn => {
    if (btn.hasAttribute('data-wishlist-open')) return;
    const handle = btn.dataset.handle || btn.closest('[data-product-handle]')?.dataset.productHandle;
    if (handle && wishlist.includes(handle)) {
      btn.classList.add('active');
      const svg = btn.querySelector('path');
      if (svg) svg.style.fill = 'var(--color-gold)';
    }
  });

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.product-card__wish, [data-wishlist]');
    if (!btn || btn.hasAttribute('data-wishlist-open') || btn.hasAttribute('data-wishlist-remove')) return;

    const handle = btn.dataset.handle || btn.closest('.product-card')?.dataset.productHandle;
    if (!handle) return;

    let list = getWishlist();
    btn.classList.toggle('active');
    const svg = btn.querySelector('path');

    if (btn.classList.contains('active')) {
      if (!list.includes(handle)) list.push(handle);
      if (svg) svg.style.fill = 'var(--color-gold)';
      showToast('Added to wishlist ♥');
    } else {
      list = list.filter(item => item !== handle);
      if (svg) svg.style.fill = 'none';
      showToast('Removed from wishlist');
    }

    setWishlist(list);
  });
}

/* ── SIZE GUIDE MODAL ────────────────────────────────────────── */
function initSizeGuide() {
  const openBtn = $('#openSizeGuide');
  const closeBtn = $('#closeSizeGuide');
  const modal = $('#sizeGuideModal');
  if (!openBtn || !modal) return;

  openBtn.addEventListener('click', () => {
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  });

  const close = () => {
    modal.style.display = 'none';
    document.body.style.overflow = '';
  };

  closeBtn?.addEventListener('click', close);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
}

/* ── COLOR SWATCHES ──────────────────────────────────────────── */
function initSwatches() {
  $$('.swatch').forEach(swatch => {
    swatch.addEventListener('click', () => {
      const group = swatch.closest('.product-card__swatches');
      if (group) {
        $$('.swatch', group).forEach(s => s.classList.remove('active'));
        swatch.classList.add('active');
      }
    });
  });
}

/* ── NEWSLETTER FORM ─────────────────────────────────────────── */
function initNewsletter() {
  $$('.newsletter__form').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const input = form.querySelector('.newsletter__input');
      const submit = form.querySelector('.newsletter__submit');
      const email = input?.value.trim();
      if (!email) return;

      submit.textContent = 'Subscribing...';
      submit.disabled = true;

      // Simulate API call
      await new Promise(r => setTimeout(r, 900));

      showToast('Thank you for subscribing! 🎉');
      form.reset();
      submit.textContent = 'Subscribe';
      submit.disabled = false;
    });
  });
}

/* ── QUANTITY BUTTONS (cart drawer) ──────────────────────────── */
function initQtyButtons() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.qty-btn');
    if (!btn) return;

    const action = btn.dataset.action;
    const line = btn.dataset.line;
    const qtySpan = btn.parentElement?.querySelector('span');
    if (!line || !qtySpan) return;

    let qty = parseInt(qtySpan.textContent, 10);
    qty = action === 'increase' ? qty + 1 : Math.max(0, qty - 1);

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: parseInt(line), quantity: qty })
      });
      qtySpan.textContent = qty;
      await updateCartCount();
      if (qty === 0) btn.closest('.cart-item')?.remove();
    } catch {}
  });
}

/* ── VARIANT SELECTOR ────────────────────────────────────────── */
function formatPKR(cents) {
  return new Intl.NumberFormat('en-PK', {
    style: 'currency',
    currency: 'PKR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function getSelectedProductVariant() {
  const dataEl = document.getElementById('productData');
  if (!dataEl) return null;

  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch {
    return null;
  }

  const { variants, options } = data;
  if (!variants?.length) return null;

  const selected = {};
  $$('.variant-btn.active').forEach((btn) => {
    selected[btn.dataset.option] = btn.dataset.value;
  });

  if (options?.length) {
    return variants.find((variant) =>
      options.every((opt, index) => selected[opt.handle] === variant[`option${index + 1}`])
    ) || null;
  }

  return variants[0] || null;
}

function applyProductVariant(variant) {
  if (!variant) return;

  const variantInput = $('#variantId');
  const atcBtn = $('[data-add-to-cart]');

  if (variantInput) variantInput.value = variant.id;
  if (atcBtn) {
    atcBtn.dataset.variantId = variant.id;
    atcBtn.disabled = !variant.available;
  }

  const priceEl = $('.product-price--main');
  if (priceEl) priceEl.textContent = formatPKR(variant.price);

  const compareEl = $('.product-price--compare');
  if (compareEl) {
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      compareEl.textContent = formatPKR(variant.compare_at_price);
      compareEl.style.display = '';
    } else {
      compareEl.style.display = 'none';
    }
  }

  const mainImg = $('#mainProductImage');
  if (mainImg && variant.featured_image?.src) {
    mainImg.src = variant.featured_image.src.replace(/(\._\d+x\d+)?(\.(jpg|jpeg|png|webp))/i, '.$2');
  }
}

function initVariantSelector() {
  $$('.variant-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const group = btn.closest('.variant-options');
      $$('.variant-btn', group).forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');

      const label = document.getElementById(`selected-${btn.dataset.option}`);
      if (label) label.textContent = btn.dataset.value;

      applyProductVariant(getSelectedProductVariant());
    });
  });

  applyProductVariant(getSelectedProductVariant());
}

/* ── LAZY IMAGES ─────────────────────────────────────────────── */
function initLazyImages() {
  if ('loading' in HTMLImageElement.prototype) return; // native
  const imgs = $$('img[loading="lazy"]');
  const imgObserver = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        const img = e.target;
        img.src = img.dataset.src || img.src;
        imgObserver.unobserve(img);
      }
    });
  });
  imgs.forEach(img => imgObserver.observe(img));
}

/* ── SMOOTH ANCHOR SCROLL ────────────────────────────────────── */
function initSmoothScroll() {
  document.addEventListener('click', e => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href || href === '#') return;
    const target = document.querySelector(href);
    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
}

/* ── CURRENCY FORMAT ─────────────────────────────────────────── */
function formatMoney(cents) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

/* ── INIT ALL ────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  initScrollAnimations();
  initStickyHeader();
  initMobileNav();
  initSearch();
  initPlaceOrder();
  initCartDrawer();
  initBuyNow();
  initAddToCart();
  initHero();
  initProductTabs();
  initProductGallery();
  initShowcaseTabs();
  initFeaturedCarousel();
  initWishlistDrawer();
  initWishlist();
  initSizeGuide();
  initSwatches();
  initNewsletter();
  initQtyButtons();
  initVariantSelector();
  initLazyImages();
  initSmoothScroll();
  initCollectionFilters();
  updateCartCount();
});

// Expose to global scope for Shopify compatibility
window.MoonlightMotifs = { showToast, updateCartCount, formatMoney };
